// services/heatmapService.js
import { apiClient, API_ENDPOINTS } from './api';
import { cacheService } from './cacheService';

export const statusConfig = {
  active: { label: "Действует", color: "#10b981", bgColor: "#d1fae5", icon: "✅", priority: 1 },
  expiring: { label: "Истекает", color: "#f59e0b", bgColor: "#fed7aa", icon: "⚠️", priority: 2 },
  expired: { label: "Просрочено", color: "#ef4444", bgColor: "#fee2e2", icon: "❌", priority: 3 },
  required: { label: "Требуется", color: "#8b5cf6", bgColor: "#ede9fe", icon: "📋", priority: 4 },
  noData: { label: "Нет данных", color: "#94a3b8", bgColor: "#f1f5f9", icon: "❓", priority: 5 }
};

export const heatmapService = {
  _isLoadingHeatmap: false,
  _heatmapPromise: null,
  _heatmapCacheTTL: 30 * 60 * 1000, // 30 минут для heatmap

  async getHeatmapData(params = {}, forceRefresh = false) {
    if (this._isLoadingHeatmap && !forceRefresh) {
      return this._heatmapPromise;
    }

    const cacheKey = cacheService.getCacheKey('heatmap_data', params);

    if (!forceRefresh) {
      const cached = cacheService.get(cacheKey, this._heatmapCacheTTL);
      if (cached) {
        return cached;
      }
    }

    try {
      this._isLoadingHeatmap = true;
      this._heatmapPromise = this._fetchHeatmapData();
      const data = await this._heatmapPromise;
      cacheService.set(cacheKey, data);
      return data;
    } finally {
      this._isLoadingHeatmap = false;
      this._heatmapPromise = null;
    }
  },

  async _fetchHeatmapData() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.HEATMAP);

      // --- Обработка trainingTypes из ответа /heatmap ---
      const trainingTypes = (response.trainingTypes || []).map(t => ({
        id: String(t.id),
        name: t.name || 'Без названия',
        shortName: t.shortName || t.short_name || t.name?.substring(0, 25) || '—',
        category_id: t.categoryId || t.category_id || null,
        category_name: t.category || t.category_name || 'Без категории'
      }));

      // --- Извлечение категорий из trainingTypes ---
      const categoriesMap = new Map();
      trainingTypes.forEach(t => {
        if (t.category_id && !categoriesMap.has(t.category_id)) {
          categoriesMap.set(t.category_id, {
            id: t.category_id,
            name: t.category_name
          });
        }
      });
      const categories = Array.from(categoriesMap.values())
        .sort((a, b) => a.id - b.id);

      // --- Обработка подразделений из ответа ---
      let departments = [];
      if (response.departments && Array.isArray(response.departments)) {
        departments = response.departments.map((dept, index) => ({
          id: String(index + 1),
          name: dept
        }));
      }

      // --- Обработка сотрудников ---
      let employees = response.employees || [];

      const normalizedEmployees = employees.map(emp => {
        const normalizedTrainings = {};
        if (emp.trainings && typeof emp.trainings === 'object') {
          Object.entries(emp.trainings).forEach(([key, value]) => {
            if (key !== '_overall') {
              normalizedTrainings[key] = value;
            }
          });
        }
        return { 
          ...emp, 
          trainings: normalizedTrainings,
          department: emp.department || 'Не указано',
          departmentId: emp.departmentId || null
        };
      });

      const sortedEmployees = normalizedEmployees.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );

      return {
        employees: sortedEmployees,
        trainingTypes,
        categories,
        departments
      };
    } catch (error) {
      console.error('Ошибка загрузки данных heatmap:', error);
      throw error;
    }
  },

  async getEmployeeHeatmap(employeeId) {
    try {
      const cacheKey = cacheService.getCacheKey(`heatmap_employee_${employeeId}`);
      const cached = cacheService.get(cacheKey, 10 * 60 * 1000);
      if (cached) {
        return cached;
      }
      
      const response = await apiClient.get(API_ENDPOINTS.HEATMAP_EMPLOYEE(employeeId));
      const data = response.data || response;

      let result;
      if (data.statistics) {
        result = {
          ...data,
          trainings: data.trainings || [],
          statistics: data.statistics,
          compliancePercentage: data.compliancePercentage ?? data.compliance_percentage ?? 0
        };
      } else {
        const trainings = data.trainings || [];
        const statistics = {
          total: trainings.length,
          active: trainings.filter(t => t.status === 'active').length,
          expiring: trainings.filter(t => t.status === 'expiring').length,
          expired: trainings.filter(t => t.status === 'expired').length,
          required: trainings.filter(t => t.status === 'required').length,
          noData: trainings.filter(t => t.status === 'noData').length
        };

        result = {
          ...data,
          trainings,
          statistics,
          compliancePercentage: statistics.total > 0
            ? Math.round((statistics.active / statistics.total) * 100)
            : 0
        };
      }
      
      cacheService.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error(`Ошибка загрузки деталей сотрудника ${employeeId}:`, error);
      throw error;
    }
  },

  async getDepartments() {
    try {
      const cacheKey = cacheService.getCacheKey('heatmap_departments');
      const cached = cacheService.get(cacheKey, 30 * 60 * 1000);
      if (cached) {
        return cached;
      }
      
      const heatmapData = await this.getHeatmapData({}, false);
      
      if (heatmapData.departments && heatmapData.departments.length > 0) {
        cacheService.set(cacheKey, heatmapData.departments);
        return heatmapData.departments;
      }
      
      const departmentsMap = new Map();
      
      if (heatmapData.employees && Array.isArray(heatmapData.employees)) {
        heatmapData.employees.forEach(employee => {
          if (employee.department && employee.department !== 'Не указано') {
            const deptId = employee.departmentId || String(employee.department).toLowerCase().replace(/\s/g, '_');
            if (!departmentsMap.has(deptId)) {
              departmentsMap.set(deptId, {
                id: deptId,
                name: employee.department
              });
            }
          }
        });
      }
      
      const result = Array.from(departmentsMap.values())
        .sort((a, b) => a.name.localeCompare(b.name));
      
      if (result.length > 0) {
        cacheService.set(cacheKey, result);
      }
      
      return result;
    } catch (error) {
      console.error('Ошибка загрузки подразделений:', error);
      return [];
    }
  }
};